import type {
  Attachment,
  VerificationStageStatus,
  VerificationStageType,
} from "@/features/operations/verifications/types.ts";

// ============================================
// Core Entity Types
// ============================================

export interface VerificationStage {
  orderItemId: number;
  orderId: number;
  projectName: string;
  attributeName: string;
  orderCreationDate: string;
  userName: string;
  stageName: string;
  stageNumber: number;
  driverName: string;
  orderItemAmount: number;
  beneficiaryName: string;
  verificationId?: number;
  verificationStageId?: number;
  verificationStageStatus?: VerificationStageStatus;
  type: VerificationStageType;
  attachments?: Attachment[];
  smsSendingDate: string;
  customerName: string;
  iftarProjectBeneficiariesNames: string[];
  isPrinted?: boolean;
  isFirstStageVerified?: boolean;
  projectClassification?: string;
}

// ============================================
// API Request Types
// ============================================

export interface UnassignDriverRequest {
  verificationStagesIds: number[];
  selectingAll: boolean;
  searchValues: VerifySearchValues;
}
export interface UnassignDriverRequestPayload {
  verificationStagesIds?: number[];
  searchVerificationsRequestViewModel?: VerifySearchValues;
  selectingAll?: boolean;
}

export interface UpdateVerificationStatusRequest {
  verificationStagesIds: number[];
  isAccepted: boolean;
  comment?: string;
  searchValues: VerifySearchValues;
  selectingAll: boolean;
}

export interface UpdateVerificationStatusRequestPayload {
  verificationStagesIds?: number[];
  isAccepted: boolean;
  comment?: string;
  searchVerificationsRequestViewModel?: VerifySearchValues;
  selectingAll?: boolean;
}
export interface ReturnToDriverRequest {
  verificationIds: number[];
  selectingAll: boolean;
  searchValues: VerifySearchValues;
}
export interface ReturnToDriverRequestPayload {
  verificationIds?: number[];
  searchVerificationsRequestViewModel?: VerifySearchValues;
  selectingAll?: boolean;
}

export interface ResendVerificationCodeRequest {
  fromTime: string;
  toTime: string;
}

export interface GenerateVerificationStagesPdfRequest {
  verificationStaesIds: number[];
  selectingAll: boolean;
  searchValues: VerifySearchValues;
}
export interface GenerateVerificationStagesPdfRequestPayload {
  verificationStaesIds?: number[];
  searchVerificationsRequestViewModel?: VerifySearchValues;
  selectingAll?: boolean;
}

export interface MarkVerificationStagesAsPrintedRequest {
  verificationStaesIds: number[];
  selectingAll: boolean;
  searchValues: VerifySearchValues;
}
export interface MarkVerificationStagesAsPrintedRequestPayload {
  verificationStaesIds?: number[];
  searchVerificationsRequestViewModel?: VerifySearchValues;
  selectingAll?: boolean;
}


export interface PreSignZipUploadRequest {
  fileName: string;
  contentType: "application/zip";
}
export interface PreSignZipUploadResponse {
  uploadUrl: string;
  zipKey: string;
  expiresAtUtc: string;
}

export interface AddVerificationFilesRequest {
  verificationStagesIds: number[];
  selectingAll: boolean;
  searchValues: VerifySearchValues;
  photos?: File[];
  videos?: File[];
  zipFile?: File[];
  zipKey?: string;
}

export enum ZipFileStatus
{
    Pending = 0,
    Uploaded = 1,
    Failed = 2
}

export interface ZipFile {
  fileName: string;
  fileKey: string;
  url: string;
  status: ZipFileStatus;
}

export interface ZipFilesListResponse {
  data: ZipFile[];
  totalItems: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface AddVerificationFilesRequestPayload {
  verificationStagesIds?: number[];
  searchVerificationsRequestViewModel?: VerifySearchValues;
  selectingAll?: boolean;
  photos?: File[];
  videos?: File[];
  zipFile?: File | null;
}

export interface UpdateSmsSendingDateFormValues {
  dateToSendSmsNotification: Date | null;
}
// ============================================
// API Response Types
// ============================================

export interface StagesToVerifyListResponse {
  data: VerificationStage[];
  totalItems: number;
}

// ============================================
// Search & Pagination Types
// ============================================

export interface VerifySearchValues {
  verificationStageStatus: string;
  projectIds: number[];
  projectAttributeId: string;
  projectClassificationId: string;
  toTime: string;
  fromTime: string;
  stageName: string;
  driverId: string;
  smsSendingDateTimeFrom: string;
  smsSendingDateTimeTo: string;
  searchKey: string;
  isPrinted: string;
  isFirstStageVerified: string;
}

export interface VerifyQueryParams {
  page: number;
  perSize: number;
  search: VerifySearchValues;
}

// ============================================
// Form Types
// ============================================

export interface UpdateStatusFormValues {
  verificationStagesIds: number[];
  isAccepted: boolean;
  comment: string;
}

export interface ResendCodeFormValues {
  fromTime: Date | null;
  toTime: Date | null;
}

// ============================================
// Options Types
// ============================================

export interface StatusOption {
  value: VerificationStageStatus;
  name: string;
}

// Re-export shared types used by this feature
export type {
  VerificationStageStatus,
  VerificationStageType,
  Attachment,
  AttachmentType,
} from "@/features/operations/verifications/types.ts";
export type {
  PaginationState,
  ExcelHeaderCellsName,
} from "@/features/operations/verifications/types.ts";
